import { IntegrationsModule } from '@/core/modules/integrations'
import { Header } from '@/ui/layouts/dashboard/components/header'
import { useRouting } from '@/core/hooks/useRouting'
import { AppModule } from '@core/modules/app/app.module'
import { AccountModule } from '@core/modules/account/account.module'
import { UserModule } from '@core/modules/user/user.module'
import { Analytics } from '@core/services/mixpanel'
import ErrorScreen from '@layouts/dashboard/components/error-screen'
import { LoadingScreen } from '@layouts/dashboard/components/LoadingScreen'
import { clsx } from 'clsx'
import { useEffect, useSyncExternalStore } from 'react'
import { Outlet } from 'react-router-dom'
import { Sidebar } from './components/sidebar'
import './dashboard.layout.scss'
import AtBayLogo from '@assets/images/at-bay.svg?react'
import { useBreakpoints } from '@/core/hooks/useBreakpoints/useBreakpoints'
import { useCustomTheme } from '@/core/theme'

const DashboardLayout = () => {
    const theme = useCustomTheme()
    const { location, navigate, shouldHideSidebar } = useRouting()
    const { loading, error } = AppModule.useStore()
    const { accountType } = AccountModule.useStore()
    const { currentUser } = UserModule.useStore()
    const { aboveBreakpoint } = useBreakpoints()

    const isAccountTypeResolving = !accountType

    const subscribeOnLS = (listener: {
        (this: Window, ev: StorageEvent): any
        (this: Window, ev: StorageEvent): any
    }) => {
        window.addEventListener('storage', listener)
        return () => {
            window.removeEventListener('storage', listener)
        }
    }

    const handleGoogleIntegration = () => {
        const googleIntegrationData = localStorage.getItem(
            '/googleIntegrationData'
        )
        const googleParsedData =
            googleIntegrationData && JSON.parse(googleIntegrationData)

        if (googleParsedData) {
            const { GoogleTenantId, GoogleErr, GoogleCode } = googleParsedData

            if (GoogleTenantId && (GoogleCode || GoogleErr)) {
                localStorage.removeItem('/googleIntegrationData')
                localStorage.removeItem('/qs')

                if (GoogleErr) {
                    IntegrationsModule.breakIntegrationState({
                        providerId: GoogleTenantId,
                    })
                } else {
                    IntegrationsModule.activateIntegration({
                        providerId: GoogleTenantId,
                        code: GoogleCode,
                    })
                }
            }
        }
    }

    const handleMicrosoftIntegration = () => {
        const msIntegrationData = localStorage.getItem('/msIntegrationData')
        const msParsedData = msIntegrationData && JSON.parse(msIntegrationData)

        if (msParsedData) {
            const { msProviderId, msTenantId, integrationErr } = msParsedData

            if (msProviderId || msProviderId === '') {
                localStorage.removeItem('/msIntegrationData')
                localStorage.removeItem('/qs')

                if (integrationErr) {
                    IntegrationsModule.breakIntegrationState({
                        providerId: msProviderId,
                        error: integrationErr,
                    })
                } else if (msTenantId) {
                    IntegrationsModule.activateIntegration({
                        providerId: msProviderId,
                        tenantId: msTenantId,
                    })
                } else {
                    IntegrationsModule.pollDataIfNeeded({
                        providerId: msProviderId,
                    })
                }
            }
        }
    }

    const getSnapShot = () => {
        if (!localStorage || window.location.pathname === '/') {
            // prevent race condition in both child and parent LS listener
            return
        }

        handleMicrosoftIntegration()
        handleGoogleIntegration()
    }

    useSyncExternalStore(subscribeOnLS, getSnapShot)

    useEffect(() => {
        AppModule.onUserEnterRoute().catch(console.error)
    }, [location])

    /**
     * Redirect listener
     * @event redirect {@link SEMEvents.redirect}
     */
    useEffect(() => {
        const unbind = AppModule.$eventBus.on('redirect', (route) => {
            navigate(route)
        })
        return () => unbind()
    }, [navigate])

    if (loading || isAccountTypeResolving) {
        return <LoadingScreen />
    }

    if (error) {
        return (
            <div className="layout dashboard-layout error">
                <div
                    className={clsx(
                        'dashboard-layout-parts',
                        shouldHideSidebar &&
                            'dashboard-layout-parts--no-sidebar'
                    )}
                >
                    {!shouldHideSidebar && (
                        <Sidebar classAddon="dashboard-layout-parts-side-bar" />
                    )}
                    <Header
                        error={error}
                        showAppNavigation={!shouldHideSidebar}
                    />
                    <main className="container">
                        <ErrorScreen />
                    </main>
                </div>
            </div>
        )
    }

    if (!loading && currentUser) {
        Analytics.event('SEM::dashboard_layout_render', {
            route: location.pathname,
        })
        return (
            <div className={clsx('layout', 'dashboard-layout')}>
                <div
                    className={clsx(
                        'dashboard-layout-parts',
                        shouldHideSidebar &&
                            'dashboard-layout-parts--no-sidebar'
                    )}
                >
                    <Header showAppNavigation={!shouldHideSidebar} />
                    {!shouldHideSidebar && (
                        <Sidebar classAddon="dashboard-layout-parts-side-bar" />
                    )}
                    <main className="container">
                        <Outlet />
                    </main>
                    {shouldHideSidebar && aboveBreakpoint('xl') && (
                        <div className="logo-container">
                            <AtBayLogo
                                width={57}
                                height={32}
                                color={theme.colors.greys[900]}
                            />
                        </div>
                    )}
                </div>
            </div>
        )
    } else {
        return null
    }
}

export default DashboardLayout
